import React from "react";
import { Strings } from '--client/../strings/en';
import { CreateTypedSlateEditorBase } from "--client/../slate/editors/TypedSlateEditorBase";
import { MessageInputInnerMessage } from "../../../slate/MessageInput/components/InnerMessage";
import { SlateRoutingMessageDocument } from "--client/../static_types/routing/common.types";
import { TopicValue } from "--client/../static_types/topic.types";
import { useLoggedInUser } from "--client/common/hooks/useLoggedInUser";
import { DisplayNameFromID } from "--floponents-web/atoms/DisplayName";
import { StyledTopicDisplayName, TopicDisplayNameFromID } from "--floponents-web/atoms/DisplayName";
import styled from "styled-components";
import { CallParticipantSlateDocument } from "--client/../static_types/slate/CallParticipant";
import { NoSpaceId } from "--client/../utils";
import { useAPICallState } from "../../../common/api/useAPICall";
import { SwaggerQueryClient } from "--client/common/apis/rest";
interface MessageToMessageMutationProps {
    currentMessage: SlateRoutingMessageDocument | CallParticipantSlateDocument,
    topic?: TopicValue & { id: string }
}

const StyledWarning = styled.div`
    display: flex;
    gap: ${p => p.theme.spacing(0.5)};
    flex-wrap: wrap;
    line-height: 1.2em;

    > div {
        display: flex;
        gap: ${p => p.theme.spacing(0.5)};
        flex-basis: 100%; // $JS - Force divs on separate lines
    }
`
const StyledPlusMore = styled.span`
    color: ${p => p.theme.palette.text.secondary};
    border: solid 1px ${p => p.theme.palette.text.secondary};
    border-radius: ${p => p.theme.radius.small};
`
export function useMessageToMessageMutation(props: MessageToMessageMutationProps) {
    const user = useLoggedInUser();

    if (user.type === "cloud")
        return null;//early return is deterministic
    const { currentMessage, topic } = props;
    const editor = React.useMemo(() => CreateTypedSlateEditorBase({ children: currentMessage }), [currentMessage]);

    const topicMentions = React.useMemo(() => editor.getNodes({
        map: v => v.type === "topic" && v.target !== "stream" ? v : undefined
    }), [editor]);
    const xpostMentions = React.useMemo(() => editor.getNodes({
        map: v => v.type === "topic" && v.target === "stream" ? v : undefined
    }), [editor]);

    const peopleMentions = React.useMemo(() => editor.getNodes({
        map: v => v.type === "user" ? v : undefined
    }), [editor]);
    const combinedMentions = React.useMemo(
        () => [...peopleMentions, ...topicMentions],
        [JSON.stringify(peopleMentions), JSON.stringify(topicMentions)]
    )
    const [mutation] = useAPICallState(() => props.topic && (combinedMentions.length || props.topic.type.name === "stream" && props.topic.type.subtype === "spaceDM" || props.topic.id === NoSpaceId)
        ? SwaggerQueryClient.GetMutationsFromMentions({
            mentions: combinedMentions,
            topic_id: props.topic?.id
        })
        : null, combinedMentions);


    const currentUser = useLoggedInUser();
    //offnet members can't mutate or invite others or create new streams
    return React.useMemo((): React.ReactNode | null => {
        if (mutation || xpostMentions.length !== 0) {
            if (!topic) return null
            const peopleMentionsMutating = mutation?.actions.reduce((acc, v) => v.type === "add-member" ? [...acc, ...v.users] : acc, []) ?? [];
            const topicMentionsMutating = mutation?.actions.reduce((acc, v) => v.type === "add-guest-space" ? [...acc, v] : acc, []) ?? [];
            const visibleTopicMutations = topicMentionsMutating.reduce((acc, v) => mutation?.type !== "create-stream" || v.guestSpaceId !== mutation.spaceId ? [...acc, v.guestSpaceId] : acc, []);
            const visiblePeopleMutations = peopleMentionsMutating.reduce((acc, v) => v.user_id !== currentUser.user_or_identity_id ? [...acc, v.user_id] : acc, []);

            const newMentions = Array.from(new Set(xpostMentions.filter(v => v.id === "").map(v => v.children[0].text)));
            const oldMentions = Array.from(new Set(xpostMentions.filter(v => v.id !== "").map(v => v.id)));

            return <MessageInputInnerMessage level={mutation?.type === "update" ? "warning" : "info"}>
                <StyledWarning>
                    {
                        xpostMentions.length
                            ? <div>
                                <Strings.Inline>
                                    <span>Crosspost to</span>
                                    <MentionPlusOthers type="topic" mentionIds={oldMentions} />
                                    {oldMentions.length && newMentions.length
                                        ? <span>&</span>
                                        : null}
                                    <MentionPlusOthers type="stream" names={newMentions} />
                                </Strings.Inline>
                            </div>
                            : null
                    }
                    <div>
                        {
                            mutation?.type === "create-stream"
                                ? <>
                                    <span>+Stream in <MentionPlusOthers type="topic" mentionIds={[mutation.spaceId]} /></span>
                                    {visiblePeopleMutations.length || visibleTopicMutations.length
                                        ? <span>with</span>
                                        : null}
                                </>
                                : null
                        }
                        <Strings.Inline>
                            <MentionPlusOthers type="topic" mentionIds={visibleTopicMutations} />
                            {visiblePeopleMutations?.length && visibleTopicMutations?.length
                                ? <span>&</span> : null}
                            <MentionPlusOthers type="user" mentionIds={visiblePeopleMutations} />
                            {mutation?.type === "update"
                                ? <span>joins.</span>
                                : null
                            }
                        </Strings.Inline>
                    </div>
                </StyledWarning>
            </MessageInputInnerMessage>
        }
        return null
    }, [mutation, topic, JSON.stringify(xpostMentions)]);
}

/**
 * @param type how to render the mention
 * @returns first mention in array, `+${length - 1} others`
 */
const MentionPlusOthers: React.FC<{ type: "user" | "topic", mentionIds: string[] } | { type: "stream", names: string[] }> = React.memo(props => {
    const MentionRenderer = props.type === "user" ? DisplayNameFromID : TopicDisplayNameFromID;
    const size = (props.type === "stream" ? props.names : props.mentionIds).length;
    return <>
        {
            props.type !== "stream"
                ? props.mentionIds
                    .slice(0, 1)
                    .map(
                        v =>
                            <MentionRenderer
                                variant="chip"
                                key={v}
                                id={v} userId={v}
                                colourize
                                includePrefix
                            />
                    )
                : props.names
                    .slice(0, 1)
                    .map(
                        v => <span>+<StyledTopicDisplayName
                            variant="chip"
                            type="stream"
                            key={v}
                            colorSeed={v}>
                            #{v}
                        </StyledTopicDisplayName>
                        </span>
                    )
        }
        {
            size > 1
                ? <StyledPlusMore>+{size - 1} <Strings.Inline>others</Strings.Inline></StyledPlusMore>
                : null
        }
    </>
}, (prevProps, currProps) => {
    if (prevProps.type !== currProps.type) return false;
    const [prevItems, currItems] = [
        prevProps.type === "stream" ? prevProps.names : prevProps.mentionIds,
        currProps.type === "stream" ? currProps.names : currProps.mentionIds
    ]
    if (prevItems.at(0) !== currItems.at(0)) return false;
    if (prevItems.length !== currItems.length) return false;
    return true; // $JS - props are "equal" - we only care about the type of mention, the first item and the length(+x others)
});